/**
 * Tenant Context Provider
 * 
 * Provides tenant state management for the application.
 * Handles loading user's accessible tenants, selecting current tenant,
 * and persisting the selection in localStorage.
 * 
 * IMPORTANT: This provider must be used within AuthProvider.
 * It waits for authentication to complete before loading tenants.
 */

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import apiClient from '@/lib/api-client';
import { useAuth } from '@/lib/auth';

// =============================================================================
// Types
// =============================================================================

export interface Tenant {
  /** ULID with prefix: tnt_01HXXXXXX */
  id: string;
  /** Display name */
  name: string;
  /** URL-friendly identifier */
  slug: string;
  /** User's role in this tenant */
  role: 'owner' | 'admin' | 'member';
  /** Whether this is the system tenant */
  is_system_tenant?: boolean;
}

interface TenantContextType {
  currentTenant: Tenant | null;
  availableTenants: Tenant[];
  setCurrentTenant: (tenant: Tenant) => void;
  isLoading: boolean;
  error: string | null;
  refreshTenants: () => Promise<void>;
}

// =============================================================================
// Context
// =============================================================================

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const STORAGE_KEY = 'currentTenantId';

// =============================================================================
// Provider
// =============================================================================

export function TenantProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [currentTenant, setCurrentTenantState] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTenants = useCallback(async () => {
    // Don't load if not authenticated
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Get user's accessible tenants from /auth/me
      // The endpoint returns UserInfoResponse directly (no data wrapper)
      const response = await apiClient.get<{ 
        tenants?: Tenant[];
      }>('/api/v1/auth/me');
      
      const tenants = response.data?.tenants || [];
      setAvailableTenants(tenants);
      
      // Set default tenant (from localStorage or first available)
      const savedTenantId = localStorage.getItem(STORAGE_KEY);
      const savedTenant = tenants.find((t: Tenant) => t.id === savedTenantId);
      
      if (savedTenant) {
        setCurrentTenantState(savedTenant);
        apiClient.setTenant(savedTenant.id);
      } else if (tenants.length > 0) {
        setCurrentTenantState(tenants[0]);
        apiClient.setTenant(tenants[0].id);
        localStorage.setItem(STORAGE_KEY, tenants[0].id);
      }
    } catch (err) {
      // Handle API errors gracefully - don't break the app
      const status = (err as { response?: { status?: number } })?.response?.status;
      
      if (status === 401) {
        // Authentication issue - show friendly message
        // Don't redirect, let user see the error and decide
        setError('Session expired. Please log in again to access your tenants.');
        console.warn('[TenantProvider] 401 from API - session may be invalid');
      } else if (status === 403) {
        setError('Access denied. You may not have permission to access tenants.');
        console.warn('[TenantProvider] 403 from API - permission denied');
      } else {
        setError('Unable to load tenants. Please try again later.');
        console.error('[TenantProvider] Failed to load tenants:', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Load tenants when authentication completes
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }
    
    // Only load if authenticated
    if (isAuthenticated) {
      loadTenants();
    } else {
      setIsLoading(false);
    }
  }, [authLoading, isAuthenticated, loadTenants]);

  const setCurrentTenant = useCallback((tenant: Tenant) => {
    setCurrentTenantState(tenant);
    apiClient.setTenant(tenant.id);
    localStorage.setItem(STORAGE_KEY, tenant.id);
  }, []);

  const refreshTenants = useCallback(async () => {
    await loadTenants();
  }, [loadTenants]);

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        availableTenants,
        setCurrentTenant,
        isLoading,
        error,
        refreshTenants,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to access tenant context.
 * Must be used within a TenantProvider.
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

/**
 * Hook that requires a tenant to be selected.
 * Returns the tenant or throws if none is selected (after loading completes).
 */
export function useRequiredTenant() {
  const { currentTenant, isLoading } = useTenant();
  
  if (isLoading) {
    return { tenant: null, isLoading: true };
  }
  
  if (!currentTenant) {
    throw new Error('No tenant selected');
  }
  
  return { tenant: currentTenant, isLoading: false };
}
